import { useEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router';

import { AppCard, FlowGuide } from '@/components/AppCard';
import { Button } from '@/components/Button';
import { ErrorNote } from '@/components/ErrorNote';
import { ArrowLeftIcon } from '@/components/Icons';
import { Page } from '@/components/Layout';
import { EmojiRow } from '@/components/Nearby';
import { useJoinWithCode } from '@/hooks/useJoinWithCode';
import { isEmbedded } from '@/lib/embed';
import { t, tr, useLocale } from '@/i18n';

import styles from './ConnectPage.module.css';
import shared from './pages.module.css';

const LENGTH = 6;

/**
 * The second device joins by code instead of retyping a long link: the first
 * device shows six letters (Receive on this device, or Share → Code),
 * this one types them, the two exchange keys and the bridge opens here.
 */
export function ConnectPage(): React.JSX.Element {
  const { code: language } = useLocale();
  const navigate = useNavigate();
  const [letters, setLetters] = useState<string[]>(() => Array<string>(LENGTH).fill(''));
  const { stage, error, emoji, join, confirm, cancel } = useJoinWithCode();
  const [matched, setMatched] = useState(false);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);
  // The first cell is ready to type in as soon as the page opens.
  useEffect(() => {
    inputs.current[0]?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    document.title = t('connectPage.docTitle');
  }, [language]);

  const code = letters.join('');
  const complete = code.length === LENGTH;

  const setLetter = (index: number, value: string): void => {
    const clean = value.toUpperCase().replace(/[^A-Z]/g, '');
    setLetters((current) => {
      const next = [...current];
      next[index] = clean.slice(-1);
      return next;
    });
    if (clean.length > 0 && index < LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Backspace' && letters[index] === '' && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowLeft' && index > 0) {
      inputs.current[index - 1]?.focus();
    } else if (event.key === 'ArrowRight' && index < LENGTH - 1) {
      inputs.current[index + 1]?.focus();
    } else if (event.key === 'Enter' && complete) {
      void connect();
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    const text = event.clipboardData.getData('text').toUpperCase().replace(/[^A-Z]/g, '').slice(0, LENGTH);
    if (text.length === 0) return;
    event.preventDefault();
    setLetters(Array.from({ length: LENGTH }, (_, i) => text[i] ?? ''));
    inputs.current[Math.min(text.length, LENGTH - 1)]?.focus();
  };

  const connect = async (): Promise<void> => {
    if (!complete || stage !== 'input') return;
    setMatched(false);
    const path = await join(code);
    if (path !== null) {
      void navigate(path);
      return;
    }
    setLetters(Array<string>(LENGTH).fill(''));
    inputs.current[0]?.focus();
  };

  const progress = stage === 'input' ? 1 : stage === 'waiting' ? 2 : 3;

  // a framing page must not be able to cover the emoji comparison: pairing happens on Bridge's own page
  if (isEmbedded()) {
    return (
      <Page compact>
        <p className={styles.connectLead}>
          {tr('connectPage.embedded', {
            link: (children) => (
              <a href="/connect" target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            ),
          })}
        </p>
      </Page>
    );
  }

  return (
    <Page compact>
      <AppCard guide={<FlowGuide flow="connect" progress={progress} />} testId="connect-card" busy={stage === 'waiting'}>
        <div className={shared.panelHead}>
          <Link to="/" className={shared.backBtn} aria-label={t('connectPage.back')} data-testid="back">
            <ArrowLeftIcon size={18} />
          </Link>
          <h2 className={shared.panelTitle}>{t('connectPage.title')}</h2>
        </div>
        <p className={styles.connectLead}>
          {stage === 'waiting' ? (
            emoji === null ? t('connectPage.waiting') : (matched ? t('connectPage.waitingKey') : t('connectPage.checkEmoji'))
          ) : (
            tr('connectPage.lead', { br: () => <br /> })
          )}
        </p>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void connect();
          }}
        >
          <div className={[styles.cells, styles.cellsLeft].join(' ')} role="group" aria-label={t('connectPage.codeLabel')}>
            {letters.map((letter, index) => (
              <span key={index} style={{ display: 'contents' }}>
                {index === 3 ? <span className={styles.dash} aria-hidden="true" /> : null}
                <input
                  ref={(element) => {
                    inputs.current[index] = element;
                  }}
                  className={styles.cell}
                  value={letter}
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="one-time-code"
                  maxLength={1}
                  aria-label={t('connectPage.letterLabel', { n: index + 1, total: LENGTH })}
                  disabled={stage !== 'input'}
                  onChange={(event) => {
                    setLetter(index, event.target.value);
                  }}
                  onKeyDown={(event) => {
                    onKeyDown(index, event);
                  }}
                  onPaste={onPaste}
                  onFocus={(event) => {
                    event.target.select();
                  }}
                  data-testid={`cell-${String(index)}`}
                />
              </span>
            ))}
          </div>
          {emoji !== null ? (
            <div className={styles.connectEmoji}>
              <EmojiRow emoji={emoji} testId="connect-emoji" />
              <Button
                type="button"
                disabled={matched}
                onClick={() => {
                  setMatched(true);
                  confirm();
                }}
                data-testid="connect-match"
              >
                {t('connectPage.match')}
              </Button>
              <Button type="button" variant="secondary" onClick={cancel} data-testid="connect-cancel">
                {t('connectPage.mismatch')}
              </Button>
            </div>
          ) : null}
          {error ? <ErrorNote notice={error} /> : null}
          <div className={styles.connectActions}>
            <Button type="submit" disabled={!complete || stage !== 'input'} data-testid="connect">
              {stage === 'waiting' ? t('connectPage.connecting') : t('connectPage.connect')}
            </Button>
          </div>
        </form>
        <div className={styles.connectHelp}>
          <p className={styles.connectHelpTitle}>{t('connectPage.helpTitle')}</p>
          <p>
            {tr('connectPage.help', { receive: (children) => <b>{children}</b>, share: (children) => <b>{children}</b> })}
          </p>
        </div>
      </AppCard>
    </Page>
  );
}
